import React from 'react';

import { useParams, Link } from 'react-router-dom';
import { useI18n } from '@/i18n/LanguageContext';
import Seo from '@/components/Seo';
import NotFound from '@/pages/NotFound';
import { tradeBySlug, SEO_TRADES } from '@/data/seo/trades';
import { TOP_METRO_SLUGS, TOP_COUNTY_SLUGS, CITIES, COUNTIES } from '@/data/seo/locations';
import { MONEY_PAGES } from '@/data/seo/moneyPages';
import { RESOURCES } from '@/data/seo/resources';
import { breadcrumbSchema, faqSchema, serviceSchema, tradeFaqs, itemListSchema } from '@/lib/seoSchema';
import {
  Breadcrumbs, PageHero, AnswerBlock, Section, FaqSection, LinkGrid, CtaBand, EntityFacts, PageWrap, TrustStrip,
} from '@/components/seo/SeoBlocks';
import { LEAD_PRICE, money } from '@/data/constants';
import { STATES, stateLabel, statePath } from '@/data/seo/states';
import LiveProjects from '@/components/seo/LiveProjects';

const TradePage: React.FC = () => {
  const { slug = '' } = useParams();
  const { lang, path, t } = useI18n();
  const trade = tradeBySlug(slug);

  if (!trade) return <NotFound />;

  const name = lang === 'es' ? trade.es : trade.en;
  const answer = lang === 'es' ? trade.answerEs : trade.answerEn;
  const faqs = tradeFaqs(trade.en, trade.es, lang);
  const url = `/${lang}/trades/${trade.slug}`;

  const cityLinks = TOP_METRO_SLUGS.map((s) => CITIES.find((c) => c.slug === s))
    .filter(Boolean)
    .map((c) => ({ label: `${trade.en} in ${c!.name}, CA`, to: path(`/california/${c!.slug}`) }));

  const countyLinks = TOP_COUNTY_SLUGS.map((s) => COUNTIES.find((x) => x.slug === s))
    .filter(Boolean)
    .map((x) => ({ label: `${x!.name} County`, to: path(`/california/counties/${x!.slug}`) }));

  const moneyLinks = MONEY_PAGES.filter((m) => m.trade.slug === trade.slug)
    .slice(0, 12)
    .map((m) => ({ label: `${trade.en} Leads ${m.place}`, to: path(`/leads/${m.slug}`) }));

  const relatedTrades = SEO_TRADES.filter((x) => x.group === trade.group && x.slug !== trade.slug)
    .slice(0, 10)
    .map((x) => ({ label: lang === 'es' ? x.es : x.en, to: path(`/trades/${x.slug}`) }));

  const guides = RESOURCES.filter(
    (r) => r.slug === `how-to-bid-${trade.slug}-jobs` || r.topic === 'bidding'
  )
    .slice(0, 8)
    .map((r) => ({ label: lang === 'es' ? r.titleEs : r.title, to: path(`/resources/${r.slug}`) }));

  // Nationwide state links (all 50 states + DC) — trade pages describe U.S. coverage.
  const stateLinks = STATES.map((s) => ({ label: stateLabel(s, lang), to: path(statePath(s)) }));

  return (
    <>
      <Seo
        title={lang === 'es' ? `${name} — Proyectos y Leads en Estados Unidos` : `${trade.en} Contractors & Leads Across the U.S.`}
        description={
          lang === 'es'
            ? `${answer} Vea proyectos de ${name.toLowerCase()} publicados en todo Estados Unidos y desbloquee los que estén en su área por ${money(LEAD_PRICE)}.`
            : `${answer} Browse ${trade.en.toLowerCase()} projects posted across the United States and unlock the ones in your area for ${money(LEAD_PRICE)}.`
        }
        schema={[
          breadcrumbSchema([
            { name: 'Home', url: `/${lang}` },
            { name: 'Trades', url: `/${lang}/trades` },
            { name: trade.en, url },
          ]),
          serviceSchema({ trade: trade.en, area: 'United States', description: trade.answerEn, url }),
          faqSchema(faqs),
          itemListSchema(`${trade.en} lead pages`, moneyLinks.map((m) => ({ name: m.label, url: m.to }))),
        ]}
      />

      <PageHero
        eyebrow={t('seo.tradeEyebrow')}
        title={lang === 'es' ? `Proyectos de ${name} en Estados Unidos` : `${trade.en} Projects & Leads Across the U.S.`}
        intro={answer}
      >
        <Link to={path('/jobs')} className="rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold hover:bg-orange-600">
          {t('seo.ctaBrowse')}
        </Link>
        <Link to={path('/register')} className="rounded-xl border border-white/20 px-5 py-3 text-sm font-bold hover:bg-white/10">
          {t('seo.ctaSubBtn')}
        </Link>
      </PageHero>

      <PageWrap>
        <Breadcrumbs
          items={[
            { name: t('nav.home'), url: path('/') },
            { name: t('seo.tradesTitle'), url: path('/trades') },
            { name },
          ]}
        />

        <AnswerBlock
          question={lang === 'es' ? `¿Cómo encuentran trabajo los subcontratistas de ${name.toLowerCase()}?` : `How do ${trade.en.toLowerCase()} subcontractors find work?`}
          answer={faqs[0].a}
        />

        <EntityFacts
          rows={[
            { k: t('seo.factTrade'), v: name },
            { k: t('seo.factArea'), v: lang === 'es' ? 'Estados Unidos (50 estados + D.C.)' : 'United States (50 states + D.C.)' },
            { k: t('seo.factPrice'), v: money(LEAD_PRICE) },
            { k: t('seo.factPost'), v: t('seo.factFree') },
          ]}
        />

        <Section title={t('seo.overview')}>
          <p>{answer}</p>
          <p>{t('seo.tradeOverview', { trade: name })}</p>
          <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {trade.scopes.map((sc) => (
              <li key={sc} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700">
                {sc}
              </li>
            ))}
          </ul>
        </Section>

        <Section title={t('seo.howWorks')}>
          <ol className="space-y-3">
            <li><strong>1.</strong> {t('seo.step1')}</li>
            <li><strong>2.</strong> {t('seo.step2')}</li>
            <li><strong>3.</strong> {t('seo.step3')}</li>
            <li><strong>4.</strong> {t('seo.step4')}</li>
          </ol>
          <TrustStrip />
        </Section>

        <LiveProjects trade={trade.marketplaceTrade} heading={t('seo.openProjects', { trade: name })} />

        <FaqSection faqs={faqs} />

        <LinkGrid
          title={lang === 'es' ? `${name} por estado` : `${trade.en} by state`}
          links={stateLinks}
          icon="map"
        />
        <LinkGrid title={t('seo.leadPages', { trade: name })} links={moneyLinks} icon="map" />
        <LinkGrid title={lang === 'es' ? 'California por ciudad' : 'California by city'} links={cityLinks} icon="map" />
        <LinkGrid title={lang === 'es' ? 'California por condado' : 'California by county'} links={countyLinks} icon="map" />
        <LinkGrid title={t('seo.relatedTrades')} links={relatedTrades} icon="trade" />
        <LinkGrid title={t('seo.guides')} links={guides} columns={2} />

        <CtaBand />
      </PageWrap>
    </>
  );
};

export default TradePage;
